// Data hook for the public photo gallery. Loads the published photos of a shared
// tournament and keeps them fresh by polling every 20s and on tab focus, so new
// photos the organiser publishes appear without a reload. Mirrors
// `usePublicTournament` but is intentionally lighter: it keeps the last good list
// on a transient failure and never surfaces a hard error (the gallery simply
// stays as it was, or empty).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublicPhoto } from '../../domain';
import { fetchPublicPhotos } from '../../data/publicClient';

const POLL_INTERVAL_MS = 20_000;

export interface PublicPhotosResult {
  photos: PublicPhoto[];
  loading: boolean;
  refresh: () => void;
}

export function usePublicPhotos(id: string): PublicPhotosResult {
  const [photos, setPhotos] = useState<PublicPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const next = await fetchPublicPhotos(id);
      if (mounted.current) setPhotos(next);
    } catch {
      // Keep the last good list on a transient failure.
    } finally {
      if (mounted.current) setLoading(false);
      inFlight.current = false;
    }
  }, [id]);

  const refresh = useCallback(() => void load(), [load]);

  useEffect(() => {
    mounted.current = true;
    void load();

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      mounted.current = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  return { photos, loading, refresh };
}
